'use client'

import 'src/hooks/howlerConfig'
import { useEffect } from 'react'
import { useLocalizedNotification } from 'src/hooks/useLocalizedNotifications'
import { useGetCurrentLanguage } from 'src/store/game'
import {
  useGetCurrentNotifications,
  useGetEffectsVolume,
  useGetIsEffectsMuted,
  useGetIsMasterMuted,
  useGetMasterVolume,
  useSoundActions
} from 'src/store/sound'
import { toSafeVolume } from 'src/utils/volumeUtils'

const NOTIFICATIONS_VOLUME_MODIFIER = 0.1

// A phase voice-over queued longer than this is stale (the tab was
// backgrounded/suspended and the moment has passed) — drop it instead of
// firing it late on refocus.
const STALE_NOTIFICATION_MS = 1500

export function SoundNotifications() {
  const currentLanguage = useGetCurrentLanguage()

  const localizedNotification = useLocalizedNotification(currentLanguage)
  const soundStoreActions = useSoundActions()

  // Volume state
  const masterVolume = useGetMasterVolume()
  const effectsVolume = useGetEffectsVolume()
  const currentSoundNotifications = useGetCurrentNotifications()
  const volume = toSafeVolume(effectsVolume * masterVolume * NOTIFICATIONS_VOLUME_MODIFIER, 0)

  // Muted state
  const isMasterMuted = useGetIsMasterMuted()
  const isEffectsMuted = useGetIsEffectsMuted()
  const muted = isEffectsMuted || isMasterMuted

  // Play sound notifications. Phase VOs ("no more bets") are time-sensitive:
  //  - never play while the tab is hidden — a play() onto a suspended
  //    AudioContext is deferred and re-fires on resume (the stale cue), and
  //  - never play a cue that has sat in the queue too long — the tab was
  //    backgrounded/suspended and the moment has passed.
  // Either way we still CLEAR it so nothing lingers to replay. Cues are id'd
  // with Date.now() (sound store), so age = now - id.
  useEffect(() => {
    if (!localizedNotification) return
    if (currentSoundNotifications.length === 0) return

    const hidden = typeof document !== 'undefined' && document.hidden
    const now = Date.now()
    currentSoundNotifications.forEach((notification) => {
      const ageMs = now - Number(notification.id)
      const stale = !Number.isNaN(ageMs) && ageMs > STALE_NOTIFICATION_MS
      if (!hidden && !stale) {
        localizedNotification.play(notification.type)
      }
      soundStoreActions.clearNotification(notification.id)
    })
  }, [localizedNotification, currentSoundNotifications, soundStoreActions])

  // Flush on hide. React can defer this passive effect while the tab is hidden,
  // so the document.hidden guard above may not run until the tab is visible
  // again (when document.hidden is already false). A direct visibilitychange
  // handler flushes the store queue AND stops anything already scheduled on the
  // Howl the instant the tab hides, closing that gap.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        soundStoreActions.clearAllNotifications()
        localizedNotification?.stop()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [localizedNotification, soundStoreActions])

  // Set volume
  useEffect(() => {
    if (localizedNotification) {
      localizedNotification.volume(muted ? 0 : volume)
    }
  }, [localizedNotification, volume, muted])

  return null
}
